import { Hammer, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Animated "working" indicator for apply/publish actions — a tapping tool icon
 * (hammer by default) that signals work is in progress. Coral theme tokens;
 * honors prefers-reduced-motion (the icon holds still). See `.animate-hammer-tap`
 * in index.css.
 *
 * Reusable across the app — pass a different `icon` (any LucideIcon) to fit the
 * action (e.g. UploadCloud for publish) while keeping the same animation + shell.
 *
 * Two pieces:
 *  - <FixingLoader/>  — inline (e.g. inside a button, replacing a spinner).
 *  - <FixingOverlay/> — a brief contained overlay over its nearest positioned
 *    parent (give that parent `relative`).
 */

interface FixingLoaderProps {
  /** Visible + screen-reader label. */
  label?: string;
  /** Icon size in px. */
  size?: number;
  /** Icon only (e.g. inside a compact button) — label still announced to SR. */
  iconOnly?: boolean;
  /** Tool icon to animate. Defaults to a hammer. */
  icon?: LucideIcon;
  className?: string;
}

export function FixingLoader({
  label = "Fixing…",
  size = 16,
  iconOnly = false,
  icon: Icon = Hammer,
  className,
}: FixingLoaderProps) {
  return (
    <span
      className={cn("inline-flex items-center gap-1.5", className)}
      role="status"
      aria-live="polite"
    >
      <Icon
        className="animate-hammer-tap text-coral"
        style={{ width: size, height: size }}
        aria-hidden="true"
      />
      {iconOnly ? (
        <span className="sr-only">{label}</span>
      ) : (
        <span>{label}</span>
      )}
    </span>
  );
}

interface FixingOverlayProps {
  /** When true, render the overlay. */
  show: boolean;
  label?: string;
  /** Tool icon to animate. Defaults to a hammer. */
  icon?: LucideIcon;
  className?: string;
}

export function FixingOverlay({
  show,
  label = "Applying fix…",
  icon: Icon = Hammer,
  className,
}: FixingOverlayProps) {
  if (!show) return null;
  return (
    <div
      className={cn(
        "absolute inset-0 z-50 flex flex-col items-center justify-center gap-3",
        "rounded-xl bg-warm-bg/85 backdrop-blur-sm",
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-coral-light shadow-sm">
        <Icon
          className="animate-hammer-tap text-coral"
          style={{ width: 28, height: 28 }}
          aria-hidden="true"
        />
      </span>
      <span className="text-sm font-medium text-coral-dark">{label}</span>
    </div>
  );
}
